import React, { useEffect, useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import {
  AttendanceEntitiesCompensationBlockCompensationPoolEnum,
  AttendanceEntitiesCompensationBlockStatusEnum,
  AttendanceSidepanelCompensationBlockFragment,
  FetchCompensationOverviewSidepanelDocument,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { useQuery } from 'modules/core/hooks/useQuery'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import FlexibleCompensationAllocation from 'modules/attendance/components/ClockIn/TabsBreakdown/CompensationPannel/FlexibleCompensationAllocation'
import EmptyState from 'modules/attendance/components/ClockIn/TabsBreakdown/CompensationPannel/TimeCategoriesBreakdown/EmptyState'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { BOHAllocationState } from 'modules/attendance/lib/types'
import CompensationBlockUtils from 'modules/attendance/lib/utils/CompensationBlockUtils'

import SummaryCard from './SummaryCard'
import TimeBlockEntry from './TimeBlockEntry'
import TimeBlockGroup from './TimeBlockGroup'
import { useSelectionManager } from './useSelectionManager'

type Props = {
  setSelectedCompensationBlocks: React.Dispatch<
    React.SetStateAction<AttendanceSidepanelCompensationBlockFragment[]>
  >
  employeeId: string
  useComplementaryHoursLabels?: boolean
  onHasErrorsChange?: (hasErrors: boolean) => void
  payrollCompensationEnabled: boolean
  timeoffCompensationEnabled: boolean
  /** When false, manual allocation UI (FlexibleCompensationAllocation) is hidden. Comes from time tracking "Allow manual allocation" setting. */
  allowManualAllocationEnabled?: boolean
  /** Lifted state for BOH manual allocation (time off / payroll split). When set, FlexibleCompensationAllocation is controlled. */
  bohAllocation?: BOHAllocationState | null
  onBOHAllocationChange?: (allocation: BOHAllocationState) => void
}

const COMPENSATION_POOLS: AttendanceEntitiesCompensationBlockCompensationPoolEnum[] = [
  'payroll',
  'timeoff',
  'balance',
]

const COMPENSATION_STATUSES: AttendanceEntitiesCompensationBlockStatusEnum[] = [
  'current_pending',
  'previous_pending',
  'available',
  'compensated',
]

export const compensationTypeTranslated = (
  compensationPool: AttendanceEntitiesCompensationBlockCompensationPoolEnum
) => {
  const translations: Record<AttendanceEntitiesCompensationBlockCompensationPoolEnum, string> = {
    payroll: i18n.t('clock_in.sidepanel.compensation_overview.compensation_types.payroll'),
    timeoff: i18n.t('clock_in.sidepanel.compensation_overview.compensation_types.time_off'),
    balance: i18n.t('clock_in.sidepanel.compensation_overview.compensation_types.balance'),
  }

  return translations[compensationPool] || compensationPool
}

const getTimeBlockLabel = (
  block: AttendanceSidepanelCompensationBlockFragment,
  useComplementaryHoursLabels: boolean
) => {
  const name = CompensationBlockUtils.name(block)

  if (name === 'extra_hours_from_bank_of_hours') {
    return i18n.t('clock_in.sidepanel.compensation_overview.extra_hours_from_bank_of_hours')
  }

  if (name === 'extra_hours') {
    return i18n.t('clock_in.sidepanel.compensation_overview.extra_hours')
  }

  return CompensationBlockUtils.adjustCategoryNameForEmployeeType(name, useComplementaryHoursLabels)
}

const toTestIdSuffix = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const Overview = ({
  setSelectedCompensationBlocks,
  employeeId,
  useComplementaryHoursLabels: useComplementaryHoursLabelsProp,
  onHasErrorsChange,
  payrollCompensationEnabled,
  timeoffCompensationEnabled,
  allowManualAllocationEnabled = true,
  bohAllocation,
  onBOHAllocationChange,
}: Props) => {
  const selectionManager = useSelectionManager()
  const { stringDateFrom: startOn, stringDateTo: endOn } = useAttendanceDateNavigator()

  const useComplementaryHoursLabels = !!useComplementaryHoursLabelsProp

  // Manual allocation UI (split BOH to time off / payroll) only when feature flag and "Allow manual allocation" setting are on
  const { featureAvailability: showManualAllocation } = useAttendanceFeatures(
    'dev_time_tracking_manual_allocation'
  )
  const showFlexibleAllocation = showManualAllocation && allowManualAllocationEnabled

  const { data, loading } = useQuery(FetchCompensationOverviewSidepanelDocument, {
    variables: {
      employeeId,
      endOn,
      startOn,
    },
  })

  const renderTimeBlockEntries = (blocks: AttendanceSidepanelCompensationBlockFragment[]) => {
    return blocks.map((entry) => {
      const isSelectable = entry.minutes > 0
      return (
        <TimeBlockEntry
          key={entry.id}
          label={getTimeBlockLabel(entry, useComplementaryHoursLabels)}
          testId={`timesheet-compensation-entry-${toTestIdSuffix(
            getTimeBlockLabel(entry, useComplementaryHoursLabels)
          )}`}
          note={
            CompensationBlockUtils.isPayroll(entry) ? CompensationBlockUtils.multiplier(entry) : ''
          }
          hours={minutesToText(entry.minutes)}
          id={entry.id}
          selectable={isSelectable}
        />
      )
    })
  }

  const isCompensationPool = (
    key: string
  ): key is AttendanceEntitiesCompensationBlockCompensationPoolEnum => {
    return COMPENSATION_POOLS.some((pool) => pool === key)
  }

  const renderTimeBlockGroups = (
    blocksByType: Record<string, AttendanceSidepanelCompensationBlockFragment[]>
  ) => {
    return Object.keys(blocksByType)
      .filter(isCompensationPool)
      .map((compensationPool) => {
        const blocks = blocksByType[compensationPool]
        const title = compensationTypeTranslated(compensationPool)

        return (
          <TimeBlockGroup
            key={compensationPool}
            groupId={compensationPool}
            title={title}
            totalHours={minutesToText(CompensationBlockUtils.calculateTotalMinutes(blocks))}
          >
            {renderTimeBlockEntries(blocks || [])}
          </TimeBlockGroup>
        )
      })
  }

  // Memoized calculations
  const compensationData = useMemo(() => {
    const compensationBlocks = data?.attendance.compensationBlocksConnection.edges?.map(
      (edge) => edge.node
    )
    const compensationBlocksByStatus = groupBy(compensationBlocks, 'status')

    const previousPending = compensationBlocksByStatus.previous_pending
    const currentPending = compensationBlocksByStatus.current_pending
    const available = compensationBlocksByStatus.available
    const compensated = compensationBlocksByStatus.compensated

    return {
      previous_pending: {
        title: i18n.t('clock_in.sidepanel.compensation_overview.pending_compensations'),
        dotColor: '#5596F6',
        selectable: true,
        blocks: previousPending,
        totalHours: minutesToText(CompensationBlockUtils.calculateTotalMinutes(previousPending)),
        blocksByType: groupBy(previousPending, 'compensationPool'),
        tooltip: {
          label: i18n.t('clock_in.sidepanel.compensation_overview.tooltip.pending_label'),
          description: i18n.t(
            'clock_in.sidepanel.compensation_overview.tooltip.pending_description'
          ),
        },
      },
      current_pending: {
        title: i18n.t('clock_in.sidepanel.compensation_overview.pending_compensations'),
        dotColor: '#5596F6',
        selectable: true,
        blocks: currentPending,
        totalHours: minutesToText(CompensationBlockUtils.calculateTotalMinutes(currentPending)),
        blocksByType: groupBy(currentPending, 'compensationPool'),
        tooltip: {
          label: i18n.t('clock_in.sidepanel.compensation_overview.tooltip.pending_label'),
          description: i18n.t(
            'clock_in.sidepanel.compensation_overview.tooltip.pending_description'
          ),
        },
      },
      available: {
        title: i18n.t('clock_in.sidepanel.compensation_overview.available_compensations'),
        dotColor: '#F5A51C',
        selectable: true,
        blocks: available,
        totalHours: minutesToText(CompensationBlockUtils.calculateTotalMinutes(available)),
        blocksByType: groupBy(available, 'compensationPool'),
        tooltip: {
          label: i18n.t('clock_in.sidepanel.compensation_overview.tooltip.available_label'),
          description: i18n.t(
            'clock_in.sidepanel.compensation_overview.tooltip.available_description'
          ),
        },
      },
      compensated: {
        title: i18n.t('clock_in.sidepanel.compensation_overview.compensated'),
        dotColor: '#07A2AD',
        selectable: false,
        blocks: compensated,
        totalHours: minutesToText(CompensationBlockUtils.calculateTotalMinutes(compensated)),
        blocksByType: groupBy(compensated, 'compensationPool'),
        tooltip: {
          label: i18n.t('clock_in.sidepanel.compensation_overview.tooltip.compensated_label'),
          description: i18n.t(
            'clock_in.sidepanel.compensation_overview.tooltip.compensated_description'
          ),
        },
      },
    }
  }, [data])

  // Sync the selection state with the parent component
  useEffect(() => {
    const compensationBlocks =
      data?.attendance.compensationBlocksConnection.edges?.map((edge) => edge.node) ?? []
    const selectableBlocks = compensationBlocks.filter((block) => block.minutes > 0)
    const selectableIds = new Set(selectableBlocks.map((block) => block.id))
    const deselectIds = [...selectionManager.selectedIds].filter((id) => !selectableIds.has(id))
    if (deselectIds.length > 0) {
      selectionManager.deselectAll(deselectIds)
    }
    const selectedCompensationBlocks = compensationBlocks.filter(
      (block) => selectionManager.selectedIds.has(block.id) && block.minutes > 0
    )

    setSelectedCompensationBlocks(selectedCompensationBlocks)
  }, [selectionManager.selectedIds, setSelectedCompensationBlocks, data])

  // Find the "Extra hours from BOH" block in available status
  const availableBOHBlock = useMemo(() => {
    const availableBlocks = compensationData.available?.blocks ?? []
    return availableBlocks.find((block) =>
      CompensationBlockUtils.isExtraHoursFromBankOfHours(block)
    )
  }, [compensationData])

  const renderSummaryCard = (status: AttendanceEntitiesCompensationBlockStatusEnum) => {
    const statusData = compensationData[status]

    if (!statusData.blocks || statusData.blocks.length === 0) return null

    // Check if this is the "available" status and BOH block is selected
    const isAvailableStatus = status === 'available'
    const isBOHBlockSelected =
      isAvailableStatus && availableBOHBlock
        ? selectionManager.selectedIds.has(availableBOHBlock.id)
        : false

    return (
      <SummaryCard
        title={statusData.title}
        dateRange={CompensationBlockUtils.getCompensationDateRange({
          startOn,
          endOn,
          blocks: statusData.blocks,
        })}
        totalHours={statusData.totalHours}
        dotColor={statusData.dotColor}
        selectable={statusData.selectable}
        selectionManager={statusData.selectable ? selectionManager : undefined}
        tooltip={statusData.tooltip}
      >
        {renderTimeBlockGroups(statusData.blocksByType)}
        {isBOHBlockSelected && availableBOHBlock && showFlexibleAllocation && (
          <FlexibleCompensationAllocation
            bohBlock={availableBOHBlock}
            onHasErrorsChange={onHasErrorsChange}
            payrollCompensationEnabled={payrollCompensationEnabled}
            timeoffCompensationEnabled={timeoffCompensationEnabled}
            timeOffMinutes={bohAllocation?.timeOffMinutes}
            payrollMinutes={bohAllocation?.payrollMinutes}
            onTimeOffChange={
              onBOHAllocationChange
                ? (m) =>
                    onBOHAllocationChange({
                      timeOffMinutes: m,
                      payrollMinutes: bohAllocation?.payrollMinutes ?? 0,
                    })
                : undefined
            }
            onPayrollChange={
              onBOHAllocationChange
                ? (m) =>
                    onBOHAllocationChange({
                      timeOffMinutes: bohAllocation?.timeOffMinutes ?? 0,
                      payrollMinutes: m,
                    })
                : undefined
            }
          />
        )}
      </SummaryCard>
    )
  }

  const compensationPeriodStatus =
    data?.attendance.compensationPeriodStatusesConnection.edges?.[0]?.node?.status

  if (loading)
    return (
      <Box padding="s16" flexDirection="row" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    )

  if (compensationPeriodStatus === 'incompatible_period') {
    return (
      <EmptyState
        header={i18n.t('clock_in.sidepanel.compensation_overview.not_compatiable_period_header')}
        body={i18n.t('clock_in.sidepanel.compensation_overview.not_compatiable_period_body')}
      />
    )
  }

  if (compensationPeriodStatus === 'in_progress') {
    return (
      <EmptyState
        header={i18n.t('clock_in.sidepanel.compensation_overview.in_progress_header')}
        body={i18n.t('clock_in.sidepanel.compensation_overview.in_progress_body')}
      />
    )
  }

  const emptyState = data?.attendance.compensationBlocksConnection.edges?.length === 0

  if (emptyState)
    return (
      <EmptyState
        header={i18n.t('clock_in.sidepanel.compensation_overview.empty_state_header')}
        body={i18n.t('clock_in.sidepanel.compensation_overview.empty_state_body')}
      />
    )

  return <>{COMPENSATION_STATUSES.map((status) => renderSummaryCard(status))}</>
}

export default Overview
